// @flow

import _ from 'lodash';
import React, { type ComponentType } from 'react';
import { connect } from 'react-redux';

import * as Profile from 'app/entities/Profile';
import AccountActions from 'app/account/AccountActions';
import LoadingBlock from 'app/common/components/LoadingBlock';
import LoginActions from 'app/login/LoginActions';
import LoginFormWrapper from 'app/login/components/LoginFormWrapper';
import clearErrors from 'app/login/components/clearErrors';
import matchesMediaQuery from 'app/common/components/matchesMediaQuery';
import scrollTop from 'app/common/components/scrollTop';
import { getQueryParam } from 'app/common/utils/RouterSelectors';

import WithInvitationByCode from './WithInvitationByCode';

type Props = {
  FormComponent: ComponentType<any>,
  invitationCode: ?string,
  isInviting: boolean,
  isLoading: boolean,
  isLoggedIn: boolean,
  matchesMediaQuery: {
    narrow: boolean,
  },
};

const mapStateToProps = () => {
  // Pretty gross, but we need to know whether the component has mounted or not.
  // The reason is because `authenticationMiddleware` will have already loaded
  // `Profile` (and `Account`) and the collection's `status` might be `success`
  // which would cause `Profile.selectors.all.isLoadImpending(state)` to be
  // `false`, which would thereby cause `LoadingBlockOrComponent` to mount
  // the _non-loading_ component. However `onLoad` will still be called by
  // `MaybeLoad` which will force `Profile` to reload (as intended) which will
  // _then_ put it into a loading state, thereby _unmounting_ the non-loading
  // component, mounting the loading component, and then—once complete—remounting
  // the non-loading component. In short, the non-loading component will be
  // mounted twice instead of once like it should be. This is problematic if
  // the mounting component does something that should only happen once.
  let hasMounted = false;

  return (state, props) => {
    const invitationCode = getQueryParam(state, 'invitation');

    hasMounted = true;

    return {
      ...props,
      invitationCode,
      isInviting: Boolean(invitationCode),
      isLoading: !hasMounted || Profile.selectors.all.isLoadImpending(state),
      isLoggedIn: Profile.selectors.all.isLoggedIn(state),
    };
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    onLoad() {
      // If a valid login is sitting in the store, then we need to clear it out.
      // Otherwise, we'll think we just submitted the login.
      dispatch(LoginActions.resetForm('loginPage'));
      dispatch(AccountActions.reloadAccount());
      dispatch(Profile.actions.loadCollection());
    },
  };
};

const LoginFormWrapperContainer = (props: Props) => {
  return (
    <WithInvitationByCode code={props.invitationCode}>
      {invitation => {
        if (invitation.isLoading || props.isLoading) {
          return <LoadingBlock />;
        }

        return (
          <LoginFormWrapper
            {...props}
            accountName={invitation.accountName}
            invitationEmail={invitation.email}
            teamLogoUrl={invitation.teamLogoUrl}
          />
        );
      }}
    </WithInvitationByCode>
  );
};

export default _.flowRight(
  clearErrors,
  matchesMediaQuery({ narrow: '(max-width: 600px)' }),
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  scrollTop
)(LoginFormWrapperContainer);
